import React, { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import dealsService, { Deal, DealInput } from '../../services/crm/dealsService';
import dealStagesService, { DealStage } from '../../services/crm/dealStagesService';
import activitiesService from '../../services/crm/activitiesService';
import companiesService, { Company } from '../../services/crm/companiesService';
import contactsService, { Contact } from '../../services/crm/contactsService';
import { useCrmLayout } from '../../components/crm/CRMLayout';
import type { CrmMetric } from '../../components/crm/types';
import { useCrmRoleStore, isManager } from '../../store/crmRole.store';

const DealsBoard: React.FC = () => {
  const { activeOrgId, setHeaderProps, setQuickActions, setMetrics } = useCrmLayout();
  const { role } = useCrmRoleStore();
  const navigate = useNavigate();
  const location = useLocation();

  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const scopedCompanyId = searchParams.get('companyId') ?? '';
  const scopedContactId = searchParams.get('contactId') ?? '';
  const scopedStageName = searchParams.get('stage') ?? '';

  const [stages, setStages] = useState<DealStage[]>([]);
  const [deals, setDeals] = useState<Deal[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [title, setTitle] = useState('');
  const [amount, setAmount] = useState('');
  const [stageId, setStageId] = useState('');
  const [companyId, setCompanyId] = useState('');
  const [contactId, setContactId] = useState('');
  const [closeDate, setCloseDate] = useState('');
  const [importing, setImporting] = useState(false);
  const [importResults, setImportResults] = useState<{ row: number; status: 'ok' | 'error'; message?: string }[]>([]);
  const [quickTitle, setQuickTitle] = useState<Record<string, string>>({});
  const [quickAmount, setQuickAmount] = useState<Record<string, string>>({});
  const [noteDealId, setNoteDealId] = useState<string | null>(null);
  const [noteContent, setNoteContent] = useState('');

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const createFormRef = useRef<HTMLDivElement | null>(null);
  const createTitleRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    document.title = 'CRM | Deals';
  }, []);

  const groupedDeals = useMemo(() => {
    const map: Record<string, Deal[]> = {};
    stages.forEach((stage) => {
      map[stage.id] = [];
    });
    deals.forEach((deal) => {
      const bucket = map[deal.stage_id] ?? (map[deal.stage_id] = []);
      bucket.push(deal);
    });
    return map;
  }, [deals, stages]);

  const stageLookup = useMemo(() => {
    const map = new Map<string, DealStage>();
    stages.forEach((stage) => {
      map.set(stage.id, stage);
    });
    return map;
  }, [stages]);

  const stagesToRender = useMemo(() => {
    if (!scopedStageName) {
      return stages;
    }
    const match = stages.find((stage) => stage.name.toLowerCase() === scopedStageName.toLowerCase());
    return match ? [match] : stages;
  }, [scopedStageName, stages]);

  const fetchData = useCallback(async () => {
    if (!activeOrgId) {
      setStages([]);
      setDeals([]);
      setCompanies([]);
      setContacts([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const [stageResponse, dealsResponse, companiesResponse, contactsResponse] = await Promise.all([
        dealStagesService.list(activeOrgId),
        dealsService.list({
          organization_id: activeOrgId,
          company_id: scopedCompanyId || undefined,
          contact_id: scopedContactId || undefined,
          stage_name: scopedStageName || undefined,
          limit: 200,
          offset: 0
        }),
        companiesService.list({ organization_id: activeOrgId, limit: 200, offset: 0 }),
        contactsService.list({ organization_id: activeOrgId, limit: 500, offset: 0 })
      ]);

      setStages(stageResponse.data || []);
      setDeals(dealsResponse.data || []);
      setCompanies(companiesResponse.data || []);
      setContacts(contactsResponse.data || []);
      
      // Pre-fill form if coming from company or contact page
      if (scopedCompanyId) setCompanyId(scopedCompanyId);
      if (scopedContactId) setContactId(scopedContactId);
    } catch (err: any) {
      const message = err?.message || 'Failed to load deals';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, [activeOrgId, scopedCompanyId, scopedContactId, scopedStageName]);

  const exportCsv = useCallback(() => {
    if (!deals.length) {
      toast.error('No deals to export');
      return;
    }

    const headers = ['id', 'title', 'amount', 'stage_id', 'company_id', 'contact_id', 'close_date', 'updated_at'];
    const lines = [headers.join(',')];

    deals.forEach((deal) => {
      const row = [
        deal.id || '',
        deal.title || '',
        deal.amount?.toString() || '',
        deal.stage_id || '',
        deal.company_id || '',
        deal.contact_id || '',
        deal.close_date || '',
        deal.updated_at || ''
      ].map((value) => String(value).replace(/"/g, '""'));

      lines.push(
        row
          .map((value) => (/[",\n]/.test(value) ? `"${value}"` : value))
          .join(',')
      );
    });

    const blob = new Blob([lines.join('\r\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = 'deals_export.csv';
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
  }, [deals]);

  const parseCsv = useCallback((text: string) => {
    const lines = text
      .replace(/\r\n/g, '\n')
      .replace(/\r/g, '\n')
      .split('\n')
      .map((line) => line.trim())
      .filter((line) => line.length > 0);

    if (!lines.length) {
      return [] as Record<string, string>[];
    }

    const parseLine = (line: string) => {
      const out: string[] = [];
      let current = '';
      let inQuotes = false;

      for (let index = 0; index < line.length; index += 1) {
        const char = line[index];
        if (inQuotes) {
          if (char === '"' && line[index + 1] === '"') {
            current += '"';
            index += 1;
          } else if (char === '"') {
            inQuotes = false;
          } else {
            current += char;
          }
        } else if (char === '"') {
          inQuotes = true;
        } else if (char === ',') {
          out.push(current);
          current = '';
        } else {
          current += char;
        }
      }

      out.push(current);
      return out;
    };

    const headers = parseLine(lines[0]).map((header) => header.trim());

    return lines.slice(1).map((line) => {
      const cells = parseLine(line);
      const record: Record<string, string> = {};
      headers.forEach((header, index) => {
        record[header] = (cells[index] ?? '').trim();
      });
      return record;
    });
  }, []);

  const importCsv = useCallback(async (file: File) => {
    if (!activeOrgId) {
      toast.error('Organization is not selected');
      return;
    }

    setImporting(true);
    setImportResults([]);

    try {
      const text = await file.text();
      const rows = parseCsv(text);

      if (!rows.length) {
        toast.error('No rows found');
        return;
      }

      const byId = new Map<string, Deal>();
      deals.forEach((deal) => {
        byId.set(deal.id, deal);
      });

      const results: { row: number; status: 'ok' | 'error'; message?: string }[] = [];

      for (let index = 0; index < rows.length; index += 1) {
        const row = rows[index];
        try {
          const payload: Partial<DealInput> & { organization_id: string } = {
            organization_id: activeOrgId,
          };

          const titleValue = row.title?.trim();
          if (titleValue) payload.title = titleValue;

          const stageValue = row.stage_id?.trim();
          if (stageValue) payload.stage_id = stageValue;

          const amountValue = row.amount?.trim();
          if (amountValue) {
            const numeric = Number(amountValue);
            if (!Number.isNaN(numeric)) {
              payload.amount = numeric;
            }
          }

          const companyValue = row.company_id?.trim();
          if (companyValue) payload.company_id = companyValue;

          const contactValue = row.contact_id?.trim();
          if (contactValue) payload.contact_id = contactValue;

          const closeDateValue = row.close_date?.trim();
          if (closeDateValue) payload.close_date = closeDateValue;

          const idValue = row.id?.trim();

          if (idValue && byId.has(idValue)) {
            await dealsService.update(idValue, payload);
          } else {
            if (!payload.title || !payload.stage_id) {
              throw new Error('Title and stage_id are required to create a deal');
            }
            await dealsService.create(payload as DealInput);
          }

          results.push({ row: index + 2, status: 'ok' });
        } catch (err: any) {
          results.push({ row: index + 2, status: 'error', message: err?.message || 'Failed' });
        }
      }

      setImportResults(results);

      const errors = results.filter((result) => result.status === 'error');
      if (errors.length) {
        toast.error(`Imported with ${errors.length} error(s)`);
      } else {
        toast.success('Import completed');
      }

      await fetchData();
    } catch (err: any) {
      toast.error(err?.message || 'Import failed');
    } finally {
      setImporting(false);
    }
  }, [activeOrgId, deals, fetchData, parseCsv]);

  const handleFileInputChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      void importCsv(file);
    }
    event.target.value = '';
  }, [importCsv]);

  const triggerImport = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const focusCreateForm = useCallback(() => {
    createFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    window.setTimeout(() => {
      createTitleRef.current?.focus();
    }, 250);
  }, []);

  const refreshBoard = useCallback(() => {
    void fetchData();
  }, [fetchData]);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  const createDeal = useCallback(async () => {
    if (!activeOrgId || !title.trim() || !stageId) {
      setError('Organization, title, and stage are required');
      toast.error('Organization, title, and stage are required');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await dealsService.create({
        organization_id: activeOrgId,
        title: title.trim(),
        amount: amount ? Number(amount) : undefined,
        stage_id: stageId,
        company_id: companyId || undefined,
        contact_id: contactId || undefined,
        close_date: closeDate || undefined
      });
      setTitle('');
      setAmount('');
      setStageId('');
      setCompanyId('');
      setContactId('');
      setCloseDate('');
      await fetchData();
      toast.success('Deal created');
    } catch (err: any) {
      const message = err?.message || 'Failed to create deal';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, [activeOrgId, amount, closeDate, companyId, contactId, fetchData, stageId, title]);

  const openNote = useCallback((dealId: string) => {
    setNoteDealId(dealId);
    setNoteContent('');
  }, []);

  const closeNote = useCallback(() => {
    setNoteDealId(null);
    setNoteContent('');
  }, []);

  const saveNote = useCallback(async () => {
    if (!activeOrgId || !noteDealId) {
      return;
    }

    try {
      await activitiesService.create({
        organization_id: activeOrgId,
        entity_type: 'deal',
        entity_id: noteDealId,
        type: 'note',
        content: noteContent || undefined
      });
      toast.success('Note added');
      closeNote();
    } catch (err: any) {
      toast.error(err?.message || 'Failed to add note');
    }
  }, [activeOrgId, closeNote, noteContent, noteDealId]);

  const handleDragStart = useCallback((event: React.DragEvent, dealId: string) => {
    event.dataTransfer.setData('text/plain', dealId);
  }, []);

  const handleDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
  }, []);

  const handleDropToStage = useCallback(async (event: React.DragEvent, stage: DealStage) => {
    event.preventDefault();
    const dealId = event.dataTransfer.getData('text/plain');
    if (!dealId || !activeOrgId) {
      return;
    }

    if (!isManager(role)) {
      toast.error('Only managers can move deals between stages');
      return;
    }

    try {
      await dealsService.update(dealId, { organization_id: activeOrgId, stage_id: stage.id });
      toast.success(`Moved to ${stage.name}`);

      try {
        const due = new Date();
        due.setDate(due.getDate() + 1);
        await activitiesService.create({
          organization_id: activeOrgId,
          entity_type: 'deal',
          entity_id: dealId,
          type: 'task',
          content: `Follow up after move to ${stage.name}`,
          due_at: due.toISOString()
        });
      } catch (followErr: any) {
        toast.error(followErr?.message || 'Deal moved, follow-up task failed');
      }

      await fetchData();
    } catch (err: any) {
      toast.error(err?.message || 'Failed to move deal');
    }
  }, [activeOrgId, fetchData, role]);

  const handleQuickCreate = useCallback(async (stage: DealStage) => {
    if (!activeOrgId) {
      return;
    }

    const quickTitleValue = (quickTitle[stage.id] || '').trim();
    if (!quickTitleValue) {
      toast.error('Title is required');
      return;
    }

    try {
      await dealsService.create({
        organization_id: activeOrgId,
        title: quickTitleValue,
        amount: quickAmount[stage.id] ? Number(quickAmount[stage.id]) : undefined,
        stage_id: stage.id
      });

      setQuickTitle((prev) => ({ ...prev, [stage.id]: '' }));
      setQuickAmount((prev) => ({ ...prev, [stage.id]: '' }));
      await fetchData();
      toast.success('Deal created');
    } catch (err: any) {
      toast.error(err?.message || 'Failed to create deal');
    }
  }, [activeOrgId, fetchData, quickAmount, quickTitle]);

  const hasFilters = Boolean(scopedCompanyId || scopedContactId || scopedStageName);

  const pipelineValue = useMemo(() => deals.reduce((sum, deal) => sum + (deal.amount ?? 0), 0), [deals]);

  const weightedPipeline = useMemo(() => {
    let hasProbability = false;
    const total = deals.reduce((sum, deal) => {
      const stage = stageLookup.get(deal.stage_id);
      if (stage && typeof (stage as any).probability === 'number') {
        hasProbability = true;
        const probability = ((stage as any).probability as number) / 100;
        return sum + (deal.amount ?? 0) * probability;
      }
      return sum;
    }, 0);
    return hasProbability ? total : null;
  }, [deals, stageLookup]);

  const currencyFormatter = useMemo(
    () => new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 }),
    []
  );

  useEffect(() => {
    setHeaderProps({
      title: 'Deals board',
      subtitle: 'Track opportunities by stage, prioritize follow-up, and keep your pipeline moving.'
    });

    setQuickActions([
      {
        id: 'refresh-deals',
        label: 'Refresh board',
        onClick: refreshBoard,
        disabled: loading,
        description: 'Reload the latest deals and stage configuration.',
        tone: 'secondary'
      },
      {
        id: 'new-deal',
        label: 'Add deal',
        onClick: focusCreateForm,
        description: 'Open the quick create form to capture a new opportunity.',
        tone: 'primary'
      },
      {
        id: 'import-deals',
        label: importing ? 'Importing CSV…' : 'Import deals',
        onClick: triggerImport,
        disabled: importing,
        description: 'Upload a CSV to sync pipeline updates at scale.'
      },
      {
        id: 'export-deals',
        label: 'Export CSV',
        onClick: exportCsv,
        disabled: deals.length === 0,
        description: 'Download the current board snapshot as a CSV file.'
      }
    ]);

    const metrics: CrmMetric[] = [
      {
        id: 'active-deals',
        label: 'Active deals',
        value: Intl.NumberFormat().format(deals.length),
        hint: hasFilters ? 'Count reflects applied filters' : 'All deals in your pipeline'
      },
      {
        id: 'pipeline-value',
        label: 'Pipeline value',
        value: currencyFormatter.format(pipelineValue),
        hint: 'Sum of deal amounts in the current view'
      },
      {
        id: 'weighted-pipeline',
        label: 'Weighted pipeline',
        value: weightedPipeline != null ? currencyFormatter.format(weightedPipeline) : '—',
        hint: weightedPipeline != null ? 'Probability-weighted value based on stage probabilities' : 'Assign probabilities to stages to unlock weighted totals'
      }
    ];

    setMetrics(metrics);

    return () => {
      setHeaderProps();
      setQuickActions([]);
      setMetrics([]);
    };
  }, [currencyFormatter, deals.length, exportCsv, focusCreateForm, hasFilters, importing, loading, pipelineValue, refreshBoard, setHeaderProps, setMetrics, setQuickActions, triggerImport, weightedPipeline]);

  const clearFilters = useCallback(() => {
    navigate('/crm/deals');
  }, [navigate]);

  const columnMaxHeight = 650;

  // Color scheme for deal stages
  const getStageColor = (stageName: string, index: number) => {
    const name = stageName.toLowerCase();
    
    // Match by name first
    if (name.includes('lead') || name.includes('prospect')) {
      return { bg: 'bg-slate-50', border: 'border-slate-300', header: 'bg-gradient-to-br from-slate-500 to-slate-600', text: 'text-slate-700', badge: 'bg-slate-100' };
    }
    if (name.includes('qualify') || name.includes('discovery')) {
      return { bg: 'bg-blue-50', border: 'border-blue-300', header: 'bg-gradient-to-br from-blue-500 to-blue-600', text: 'text-blue-700', badge: 'bg-blue-100' };
    }
    if (name.includes('demo') || name.includes('presentation')) {
      return { bg: 'bg-purple-50', border: 'border-purple-300', header: 'bg-gradient-to-br from-purple-500 to-purple-600', text: 'text-purple-700', badge: 'bg-purple-100' };
    }
    if (name.includes('proposal') || name.includes('quote')) {
      return { bg: 'bg-amber-50', border: 'border-amber-300', header: 'bg-gradient-to-br from-amber-500 to-amber-600', text: 'text-amber-700', badge: 'bg-amber-100' };
    }
    if (name.includes('negotiat')) {
      return { bg: 'bg-orange-50', border: 'border-orange-300', header: 'bg-gradient-to-br from-orange-500 to-orange-600', text: 'text-orange-700', badge: 'bg-orange-100' };
    }
    if (name.includes('closed') || name.includes('won')) {
      return { bg: 'bg-green-50', border: 'border-green-300', header: 'bg-gradient-to-br from-green-500 to-green-600', text: 'text-green-700', badge: 'bg-green-100' };
    }
    if (name.includes('lost') || name.includes('dead')) {
      return { bg: 'bg-red-50', border: 'border-red-300', header: 'bg-gradient-to-br from-red-500 to-red-600', text: 'text-red-700', badge: 'bg-red-100' };
    }
    
    // Fallback to index-based coloring
    const colors = [
      { bg: 'bg-slate-50', border: 'border-slate-300', header: 'bg-gradient-to-br from-slate-500 to-slate-600', text: 'text-slate-700', badge: 'bg-slate-100' },
      { bg: 'bg-blue-50', border: 'border-blue-300', header: 'bg-gradient-to-br from-blue-500 to-blue-600', text: 'text-blue-700', badge: 'bg-blue-100' },
      { bg: 'bg-purple-50', border: 'border-purple-300', header: 'bg-gradient-to-br from-purple-500 to-purple-600', text: 'text-purple-700', badge: 'bg-purple-100' },
      { bg: 'bg-amber-50', border: 'border-amber-300', header: 'bg-gradient-to-br from-amber-500 to-amber-600', text: 'text-amber-700', badge: 'bg-amber-100' },
      { bg: 'bg-orange-50', border: 'border-orange-300', header: 'bg-gradient-to-br from-orange-500 to-orange-600', text: 'text-orange-700', badge: 'bg-orange-100' },
      { bg: 'bg-green-50', border: 'border-green-300', header: 'bg-gradient-to-br from-green-500 to-green-600', text: 'text-green-700', badge: 'bg-green-100' },
      { bg: 'bg-red-50', border: 'border-red-300', header: 'bg-gradient-to-br from-red-500 to-red-600', text: 'text-red-700', badge: 'bg-red-100' },
    ];
    return colors[index % colors.length];
  };

  return (
    <div className="space-y-6">
      {hasFilters && (
        <div className="flex flex-wrap items-center gap-2 text-sm">
          {scopedCompanyId && (
            <span className="inline-flex items-center rounded-full border border-primary/30 bg-primary/5 px-3 py-1 text-primary">
              Company: {scopedCompanyId}
            </span>
          )}
          {scopedContactId && (
            <span className="inline-flex items-center rounded-full border border-purple-200 bg-purple-50 px-3 py-1 text-purple-700">
              Contact: {scopedContactId}
            </span>
          )}
          {scopedStageName && (
            <span className="inline-flex items-center rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-amber-700">
              Stage: {scopedStageName}
            </span>
          )}
          <button
            type="button"
            className="rounded-full border border-gray-200 px-3 py-1 text-gray-600 transition hover:border-primary hover:text-primary"
            onClick={clearFilters}
          >
            Clear filters
          </button>
        </div>
      )}

      <section className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-gray-100">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Pipeline board</h2>
            <p className="mt-1 text-sm text-gray-600">Drag deals between stages to keep your pipeline current and aligned with reality.</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              className="inline-flex items-center justify-center rounded-xl border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700 transition hover:border-primary hover:text-primary"
              onClick={refreshBoard}
              disabled={loading}
            >
              Refresh
            </button>
            <button
              type="button"
              className="inline-flex items-center justify-center rounded-xl border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700 transition hover:border-primary hover:text-primary"
              onClick={triggerImport}
              disabled={importing}
            >
              {importing ? 'Importing…' : 'Import CSV'}
            </button>
            <button
              type="button"
              className="inline-flex items-center justify-center rounded-xl border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700 transition hover:border-primary hover:text-primary"
              onClick={exportCsv}
              disabled={deals.length === 0}
            >
              Export CSV
            </button>
          </div>
        </div>
        {error && (
          <div className="mt-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        )}
        <input ref={fileInputRef} type="file" accept=".csv,text/csv" className="hidden" onChange={handleFileInputChange} />
      </section>

      <section className="overflow-x-auto rounded-2xl bg-gradient-to-br from-gray-50 to-white shadow-lg ring-1 ring-gray-200">
        <div className="p-6">
          {loading ? (
            <div className="flex items-center justify-center py-20 text-sm text-gray-500">Loading deals…</div>
          ) : (
            <div className="flex gap-4 overflow-x-auto pb-4" style={{ minHeight: columnMaxHeight + 100 }}>
              {stagesToRender.map((stage) => {
                const columnDeals = groupedDeals[stage.id] || [];

                return (
                  <div
                    key={stage.id}
                    className={`flex h-full min-w-[320px] flex-col gap-4 rounded-2xl border-2 ${getStageColor(stage.name, stagesToRender.indexOf(stage)).border} ${getStageColor(stage.name, stagesToRender.indexOf(stage)).bg} p-5 shadow-lg transition-all hover:shadow-xl`}
                    onDragOver={handleDragOver}
                    onDrop={(event) => handleDropToStage(event, stage)}
                  >
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className={`h-3 w-3 rounded-full ${getStageColor(stage.name, stagesToRender.indexOf(stage)).header} shadow-lg`}></div>
                          <h3 className={`text-base font-bold ${getStageColor(stage.name, stagesToRender.indexOf(stage)).text}`}>{stage.name}</h3>
                        </div>
                        <span className={`rounded-full ${getStageColor(stage.name, stagesToRender.indexOf(stage)).badge} px-3 py-1.5 text-sm font-bold ${getStageColor(stage.name, stagesToRender.indexOf(stage)).text} shadow-sm`}>
                          {columnDeals.length}
                        </span>
                      </div>
                      {typeof (stage as any).probability === 'number' && (
                        <div className={`flex items-center gap-2 text-xs ${getStageColor(stage.name, stagesToRender.indexOf(stage)).text}`}>
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z" />
                          </svg>
                          <span className="font-semibold">{(stage as any).probability}% win rate</span>
                        </div>
                      )}
                      <div className={`text-xs ${getStageColor(stage.name, stagesToRender.indexOf(stage)).text} font-medium`}>
                        {currencyFormatter.format(columnDeals.reduce((sum, d) => sum + (d.amount ?? 0), 0))}
                      </div>
                    </div>

                    {columnDeals.length === 0 ? (
                      <div className={`flex flex-1 items-center justify-center rounded-xl border-2 border-dashed ${getStageColor(stage.name, stagesToRender.indexOf(stage)).border} bg-white/80 p-8 text-center`}>
                        <div className="space-y-2">
                          <svg className={`mx-auto h-10 w-10 ${getStageColor(stage.name, stagesToRender.indexOf(stage)).text} opacity-40`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
                          </svg>
                          <p className={`text-sm font-medium ${getStageColor(stage.name, stagesToRender.indexOf(stage)).text}`}>Drop deals here</p>
                        </div>
                      </div>
                    ) : (
                      <div className="flex-1 space-y-3 overflow-y-auto pr-1" style={{ maxHeight: columnMaxHeight }}>
                        {columnDeals.map((deal) => (
                          <div
                            key={deal.id}
                            className="group flex flex-col justify-between rounded-xl border-2 border-white bg-white p-4 text-sm shadow-md transition-all hover:shadow-xl hover:scale-[1.02] cursor-move"
                            draggable
                            onDragStart={(event) => handleDragStart(event, deal.id)}
                          >
                            <div className="space-y-2">
                              <div className="flex items-start justify-between gap-2">
                                <h4 className="font-bold text-gray-900 leading-tight">{deal.title}</h4>
                                <svg className="w-5 h-5 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
                                </svg>
                              </div>
                              <div className="flex items-center gap-2">
                                <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                                  <path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z" />
                                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z" clipRule="evenodd" />
                                </svg>
                                <span className="text-base font-bold text-gray-900">
                                  {deal.amount != null ? currencyFormatter.format(deal.amount) : <span className="text-gray-400">No amount</span>}
                                </span>
                              </div>
                              {deal.company_id && (
                                <div className="flex items-center gap-1.5 text-xs text-gray-600">
                                  <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a1 1 0 110 2h-3a1 1 0 01-1-1v-2a1 1 0 00-1-1H9a1 1 0 00-1 1v2a1 1 0 01-1 1H4a1 1 0 110-2V4zm3 1h2v2H7V5zm2 4H7v2h2V9zm2-4h2v2h-2V5zm2 4h-2v2h2V9z" clipRule="evenodd" />
                                  </svg>
                                  <span className="truncate">{companies.find(c => c.id === deal.company_id)?.name || deal.company_id}</span>
                                </div>
                              )}
                              {deal.contact_id && (
                                <div className="flex items-center gap-1.5 text-xs text-gray-600">
                                  <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                                  </svg>
                                  <span className="truncate">{contacts.find(c => c.id === deal.contact_id)?.first_name || 'Contact'}</span>
                                </div>
                              )}
                            </div>
                            <div className="flex items-center gap-2 pt-3 mt-2 border-t border-gray-100">
                              <button type="button" onClick={() => openNote(deal.id)} className="flex items-center gap-1 text-xs font-semibold text-blue-600 hover:text-blue-700 transition">
                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                </svg>
                                Note
                              </button>
                              <button
                                type="button"
                                onClick={() => navigate(`/crm/deals/${encodeURIComponent(deal.id)}`)}
                                className="ml-auto flex items-center gap-1 text-xs font-semibold text-gray-600 hover:text-gray-900 transition"
                              >
                                View
                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                </svg>
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {isManager(role) && (
                      <div className="space-y-2">
                        <input
                          className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                          placeholder="Quick title"
                          value={quickTitle[stage.id] || ''}
                          onChange={(event) => setQuickTitle((prev) => ({ ...prev, [stage.id]: event.target.value }))}
                        />
                        <div className="flex items-center gap-2">
                          <input
                            className="flex-1 rounded-xl border border-gray-200 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                            placeholder="Amount (optional)"
                            value={quickAmount[stage.id] || ''}
                            onChange={(event) => setQuickAmount((prev) => ({ ...prev, [stage.id]: event.target.value }))}
                          />
                          <button
                            type="button"
                            className="rounded-xl bg-green-600 px-3 py-2 text-xs font-semibold text-white shadow-sm transition hover:bg-green-700"
                            onClick={() => handleQuickCreate(stage)}
                          >
                            + Add
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      <aside className="space-y-6" ref={createFormRef}>
          {isManager(role) && (
            <section className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-gray-100">
              <h3 className="text-sm font-semibold text-gray-700">Create deal</h3>
              <p className="mt-1 text-xs text-gray-500">Capture a new opportunity and place it directly into your pipeline.</p>
              <div className="mt-4 space-y-3">
                <input
                  ref={createTitleRef}
                  className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                  placeholder="Title"
                  value={title}
                  onChange={(event) => setTitle(event.target.value)}
                  disabled={loading}
                />
                <input
                  className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                  placeholder="Amount (optional)"
                  value={amount}
                  onChange={(event) => setAmount(event.target.value)}
                  disabled={loading}
                />
                <select
                  className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                  value={stageId}
                  onChange={(event) => setStageId(event.target.value)}
                  disabled={loading}
                >
                  <option value="">{loading ? 'Loading stages...' : stages.length === 0 ? 'No stages configured' : 'Select stage'}</option>
                  {stages.map((stage) => (
                    <option key={stage.id} value={stage.id}>{stage.name}</option>
                  ))}
                </select>
                <select
                  className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                  value={companyId}
                  onChange={(event) => {
                    setCompanyId(event.target.value);
                    // Auto-filter contacts when company changes
                    if (!event.target.value) setContactId('');
                  }}
                  disabled={loading}
                >
                  <option value="">Select company (optional)</option>
                  {companies.map((company) => (
                    <option key={company.id} value={company.id}>
                      {company.name}
                    </option>
                  ))}
                </select>
                <select
                  className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                  value={contactId}
                  onChange={(event) => {
                    setContactId(event.target.value);
                    // Auto-set company if contact selected
                    const selectedContact = contacts.find(c => c.id === event.target.value);
                    if (selectedContact && selectedContact.company_id && !companyId) {
                      setCompanyId(selectedContact.company_id);
                    }
                  }}
                  disabled={loading}
                >
                  <option value="">Select contact (optional)</option>
                  {contacts
                    .filter(contact => !companyId || contact.company_id === companyId)
                    .map((contact) => (
                      <option key={contact.id} value={contact.id}>
                        {contact.first_name} {contact.last_name} - {contact.email}
                      </option>
                    ))}
                </select>
                <input
                  className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                  type="date"
                  value={closeDate}
                  onChange={(event) => setCloseDate(event.target.value)}
                  disabled={loading}
                />
              </div>
              {stages.length === 0 && !loading && (
                <div className="mt-4 text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-xl p-3">
                  ⚠️ No deal stages configured. Please set up stages in settings before creating deals.
                </div>
              )}
              <button
                type="button"
                className="mt-4 inline-flex w-full items-center justify-center rounded-xl bg-green-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={createDeal}
                disabled={loading || stages.length === 0}
              >
                Create deal
              </button>
              {error && <p className="mt-3 text-xs text-red-600">{error}</p>}
            </section>
          )}
        </aside>

        {importResults.length > 0 && (
            <section className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-gray-100">
              <h3 className="text-sm font-semibold text-gray-700">Import results</h3>
              <p className="mt-1 text-xs text-gray-500">Rows processed: {importResults.length}. Errors: {importResults.filter((result) => result.status === 'error').length}</p>
              <div className="mt-4 max-h-60 overflow-auto rounded-xl border border-gray-100">
                <table className="min-w-full divide-y divide-gray-100 text-xs">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left font-semibold text-gray-600">Row</th>
                      <th className="px-3 py-2 text-left font-semibold text-gray-600">Status</th>
                      <th className="px-3 py-2 text-left font-semibold text-gray-600">Message</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {importResults.map((result, index) => (
                      <tr key={`${result.row}-${index}`}>
                        <td className="px-3 py-2">{result.row}</td>
                        <td className="px-3 py-2">{result.status}</td>
                        <td className="px-3 py-2">{result.message || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

      {noteDealId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md space-y-4 rounded-2xl bg-white p-6 shadow-lg">
            <div className="text-lg font-semibold text-gray-900">Add note</div>
            <textarea
              className="h-32 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
              placeholder="Note content"
              value={noteContent}
              onChange={(event) => setNoteContent(event.target.value)}
            />
            <div className="flex justify-end gap-2">
              <button
                type="button"
                className="rounded-xl border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700 transition hover:border-primary hover:text-primary"
                onClick={closeNote}
              >
                Cancel
              </button>
              <button
                type="button"
                className="rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-primary/90"
                onClick={saveNote}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DealsBoard;
